import type { CheerioAPI, Cheerio } from "cheerio";
import { isTag, isText } from "domhandler";
import type { Element, AnyNode } from "domhandler";
import {
  createTitleBlock,
  createParagraphBlock,
  createImageBlock,
  createButtonBlock,
  createDividerBlock,
  createHtmlBlock,
} from "@templatical/types";
import type {
  Block,
  DividerBlock,
  HeadingLevel,
  SpacingValue,
} from "@templatical/types";
import type { ImportReportEntry } from "./types";
import {
  parseAlignment,
  parseBorderShorthand,
  parseColor,
  parseFontFamily,
  parseFontWeight,
  parseImageBorderRadius,
  parsePxValue,
  parseStyleAttribute,
  readPaddingFromStyles,
} from "./style-parser";

const HEADING_TAGS = new Set(["h1", "h2", "h3", "h4", "h5", "h6"]);
const TEXT_TAGS = new Set(["p", "span", "div"]);

/**
 * Wrapper tags that carry layout rather than content. One of these is worth
 * descending into when it holds a table somewhere below it.
 */
const CONTAINER_TAGS = new Set(["div", "center", "main"]);

/**
 * Decides whether an element is a layout container worth descending into: a
 * wrapper tag that holds a table somewhere below it.
 *
 * The table test is what keeps the descent from widening into "descend every
 * div". `div` is in `TEXT_TAGS` above, so a container holding only copy must
 * keep that mapping and become one paragraph rather than being split into a
 * block per child.
 *
 * Lives here, with the other predicates both traversal modules consult,
 * because the body walk and the cell walk have to agree on what a container
 * is. A second copy answers the question differently the moment either is
 * edited, and the divergence shows up as a table swallowed into a paragraph
 * on whichever surface was missed.
 */
export function isTableContainer($el: Cheerio<Element>, tag: string): boolean {
  return CONTAINER_TAGS.has(tag) && $el.find("table").length > 0;
}

/**
 * Whether an element is laid out beside its siblings rather than stacked above
 * them: `display: inline-block`.
 *
 * This is the property that *makes* a set of divs a set of columns — an email
 * that wants side-by-side divs has no other way to say so, since a block-level
 * div stacks — so requiring it is the definition of the shape and not a
 * heuristic about it. Two plain divs stack vertically, and reading those as
 * columns would invent a layout the source never stated.
 */
function isSideBySide($el: Cheerio<Element>): boolean {
  return (getStyles($el).display ?? "").trim().toLowerCase() === "inline-block";
}

/**
 * The sibling containers that make up a cell's column set, or `null` when the
 * cell is not one.
 *
 * This is the one column shape with no cell count to read: a single `<td>`
 * holding one inline-block `<div>` per column. Cerberus's hybrid template
 * states four layouts this way and compiled MJML states every one of them —
 * mjml puts a whole section's columns into one cell as sibling
 * `div.mj-column-per-*` — so a row's cell count reports one column for a
 * layout that has two or three. Counting the divs is still *counting*: the
 * number of columns comes from the number of elements, and a width is only
 * ever consulted afterwards to choose between layouts of that same count.
 *
 * Four conditions, each of them a hazard a relaxed version would reintroduce:
 *
 * - Every element child must be a container. A cell mixing a column set with
 *   anything else has no column any other element belongs to.
 * - Every one must be laid out side by side (`isSideBySide`), which is what
 *   distinguishes columns from stacked content.
 * - None may be blank. A multi-column section with an empty slot is worse
 *   than the single column a cell count already gives, and the source's own
 *   spacer chrome is exactly what would fill one.
 * - No text of the cell's own may survive, for the same reason as the first
 *   condition — a bare sentence beside the columns belongs to none of them.
 *
 * Deliberately structural: it reads no width, so a column set with no declared
 * width still becomes one, and a `width:100%` — which every real column div
 * carries — can never make or break the decision.
 */
export function columnDivsOf(
  $cell: Cheerio<Element>,
  $: CheerioAPI,
): Cheerio<Element>[] | null {
  const columns: Cheerio<Element>[] = [];
  let inlineText = "";

  for (const node of $cell.contents().toArray()) {
    if (isInlineContent(node)) {
      inlineText += $(node).text();
      continue;
    }
    // Comments carry no content, and MSO conditional comments sit between
    // every pair of column divs in both real sources — skipping them is what
    // keeps the siblings adjacent.
    if (!isTag(node)) continue;

    const tag = node.tagName.toLowerCase();
    if (!CONTAINER_TAGS.has(tag)) return null;

    const $child = $(node) as unknown as Cheerio<Element>;
    if (!isSideBySide($child)) return null;
    if (isBlankCell($child)) return null;
    columns.push($child);
  }

  if (columns.length < 2) return null;
  if (inlineText.trim() !== "") return null;
  return columns;
}

/**
 * Block-level elements a container may be unwrapped down to. Only a heading:
 * a container wrapping one is the single case where mapping the container
 * gets the block's *type* wrong.
 *
 * A tag qualifies only if unwrapping it loses nothing, and `p` is the one that
 * looks like it belongs and does not. `convertParagraph` reads an element's
 * inner HTML, so unwrapping `<div><p class="lead">…</p></div>` drops the `<p>`
 * and every attribute on it, where mapping the container keeps that markup
 * inside the paragraph's content. Nothing is bought in exchange: a wrapped
 * `<p>` already maps to a paragraph, which is the right type. A heading has no
 * such cost, because `convertHeading` stores the level and the inner HTML —
 * so unwrapping routes it to the same converter a bare `<h3>` already reaches.
 *
 * `img`, `hr` and `table` stay out for the same "nothing to fix" reason: a
 * table belongs to the container descent the two traversals run, and the other
 * two would widen a heading-typing rule into image and divider mapping.
 *
 * The set is also what keeps the styling below correct — `convertHeading` is
 * the only converter reached with a container's styles, so admitting a tag it
 * does not handle would silently drop them.
 */
const UNWRAPPABLE_BLOCK_TAGS: ReadonlySet<string> = HEADING_TAGS;

/**
 * Inline formatting tags, which carry no block of their own. One of these
 * reaching a block position means the parent's text extraction stopped short —
 * it does not mean the element has no mapping, so it must never fall through
 * to the html-fallback arm.
 *
 * The hazard that keeps them listed here: a cell's inline markup and the bare
 * text nodes around it are one run of rich text. Dispatching an inline element
 * on its own emits a block whose entire content is `<br>` AND deletes every
 * text node beside it, because a walk over element children never visits
 * those. That is silent content loss — text visible in the source email never
 * reaches the template.
 *
 * `a` is excluded on purpose: whether an anchor belongs to a run depends on
 * how the source styled it, so the cell walk asks `isProseAnchor` per anchor
 * instead. Reading every `<a>` as inline here would fold a styled call to
 * action into the sentence beside it and lose the button.
 */
const INLINE_FORMATTING_TAGS = new Set([
  "br",
  "em",
  "strong",
  "i",
  "b",
  "u",
  "small",
  "sub",
  "sup",
]);

function emptyPadding(): SpacingValue {
  return { top: 0, right: 0, bottom: 0, left: 0 };
}

function tagOf(el: Element | AnyNode): string {
  if ("tagName" in el && typeof el.tagName === "string")
    return el.tagName.toLowerCase();
  return "";
}

function getStyles($el: Cheerio<Element>): Record<string, string> {
  return parseStyleAttribute($el.attr("style"));
}

/**
 * Whether an element carries anything a reader would see: text, or an element
 * that renders without text of its own.
 *
 * One rule with two readers, which is the point: it decides both whether a
 * text container is worth a block at all and whether a wrapper is worth
 * unwrapping. Two copies would let `<div><h3></h3></div>` be skipped by one
 * and turned into an empty title by the other.
 */
function hasRenderedContent($el: Cheerio<Element>): boolean {
  if (($el.text() ?? "").trim() !== "") return true;
  return $el.find("img, a").length > 0;
}

/**
 * The single element a container's whole content consists of, or `null` when
 * the container holds anything else.
 *
 * Whitespace and comments are incidental — the same reading `extractContentBlocks`
 * gives them — and `trim` counts `&nbsp;` among them, which is how the rest of
 * this module reads it (`normalizeCellText`, and through it `isBlankCell`).
 * Everything else is content: a second element, a bare word, or a rendering
 * `<br>` all mean the container holds more than one thing, and unwrapping it
 * would drop whatever was not unwrapped.
 */
function soleElementChild($el: Cheerio<Element>): Element | null {
  let found: Element | null = null;

  for (const node of $el.contents().toArray()) {
    if (isText(node)) {
      if (node.data.trim() !== "") return null;
      continue;
    }
    if (!isTag(node)) continue;
    if (found) return null;
    found = node;
  }

  return found;
}

/**
 * The styles the innermost element of a wrapper chain renders with: each
 * container's own declarations, overridden by those of the element inside it.
 *
 * The container's styles have to travel, because the wrapper is where
 * table-based email puts the colour, size, font and alignment — a plain
 * `getStyles` on the unwrapped element trades a typing defect for a styling
 * loss.
 *
 * A declaration of `inherit` states nothing of its own, so it must not shadow
 * the container's value. That is load-bearing rather than pedantic: mjml@5
 * puts every visual property on the wrapper div and writes `color: inherit` on
 * the heading inside it, so honouring the keyword literally drops the colour
 * the email actually renders with.
 */
function inheritedStyles(chain: Cheerio<Element>[]): Record<string, string> {
  const merged: Record<string, string> = { ...getStyles(chain[0]) };

  for (const $node of chain.slice(1)) {
    for (const [property, value] of Object.entries(getStyles($node))) {
      if (value.trim().toLowerCase() === "inherit") continue;
      merged[property] = value;
    }
  }

  return merged;
}

/**
 * The element that takes a container's place when the container's entire
 * meaningful content is one block-level element, together with the styles that
 * element renders with. Returns the element handed in when there is nothing to
 * unwrap.
 *
 * Generator-produced email wraps each text block in a plain `<div>` holding a
 * single block-level element — compiled MJML puts one `<h3>` inside an
 * `mj-text` body that rendered a heading. That `<div>` holds no table, so it
 * is correctly not a container to descend, and `div` is a text tag here:
 * mapping it emits a paragraph with the heading buried in its content, which
 * loses the heading's semantics, its own styling and any downstream treatment
 * of titles.
 *
 * Three constraints, each a hazard a relaxed version would reintroduce:
 *
 * - The chain must *end* on an `UNWRAPPABLE_BLOCK_TAGS` element. That is what
 *   keeps a `div.mj-column-per-*` out: its sole child is a `<table>`, and
 *   handing that to the dispatch below would html-fallback the whole column.
 *   It also leaves a chain of containers bottoming out in bare text alone, so
 *   `<div><div>copy</div></div>` keeps mapping as it did.
 * - A container holding a table is refused outright, through the same
 *   `isTableContainer` predicate the two traversals use. This decides the one
 *   case the tag test cannot — a sole child that *is* a heading, with the
 *   table below it — and both traversals descend such a container, so the
 *   subtree is theirs rather than this dispatch's.
 * - An empty wrapper is refused, so a container whose sole child renders
 *   nothing stays skipped rather than becoming an empty title.
 *
 * Bounded by DOM depth: each step moves to a child.
 */
function resolveWrappedBlock(
  $el: Cheerio<Element>,
  $: CheerioAPI,
): { $el: Cheerio<Element>; styles: Record<string, string> } {
  const unwrapped = { $el, styles: getStyles($el) };

  const chain: Cheerio<Element>[] = [$el];
  let $current = $el;

  for (;;) {
    const tag = tagOf($current[0]);
    if (!CONTAINER_TAGS.has(tag)) break;
    if (isTableContainer($current, tag)) break;

    const child = soleElementChild($current);
    if (!child) break;

    const childTag = tagOf(child);
    if (!CONTAINER_TAGS.has(childTag) && !UNWRAPPABLE_BLOCK_TAGS.has(childTag))
      break;

    $current = $(child) as unknown as Cheerio<Element>;
    chain.push($current);
  }

  if (chain.length === 1) return unwrapped;

  const $target = chain[chain.length - 1];
  if (!UNWRAPPABLE_BLOCK_TAGS.has(tagOf($target[0]))) return unwrapped;
  if (!hasRenderedContent($target)) return unwrapped;

  return { $el: $target, styles: inheritedStyles(chain) };
}

/**
 * Returns the inner HTML of `$el`.
 */
export function getInnerHtml($el: Cheerio<Element>): string {
  return $el.html() ?? "";
}

function ensureParagraphWrapped(html: string): string {
  if (!html.trim()) return "<p></p>";
  if (/<(p|h[1-6]|ul|ol|blockquote)[\s>]/i.test(html)) return html;
  return `<p>${html}</p>`;
}

function safeHtmlComment(message: string, raw: string): string {
  const escapedMessage = message
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return `<!-- ${escapedMessage} -->\n${raw}`;
}

/**
 * Heading element (h1-h6) → Title block.
 *
 * `styles` is the element's own by default and the wrapper chain's when the
 * heading was unwrapped out of a container — see `resolveWrappedBlock`.
 */
function convertHeading(
  $el: Cheerio<Element>,
  styles: Record<string, string> = getStyles($el),
): Block {
  const tag = tagOf($el[0]);
  const levelMatch = tag.match(/^h(\d)$/);
  const rawLevel = levelMatch ? Number(levelMatch[1]) : 2;
  const level: HeadingLevel = (
    rawLevel >= 1 && rawLevel <= 4 ? rawLevel : Math.min(rawLevel, 4)
  ) as HeadingLevel;

  const innerHtml = getInnerHtml($el);
  const content = innerHtml.trim() ? `<p>${innerHtml}</p>` : "<p></p>";

  return createTitleBlock({
    content,
    level,
    color: parseColor(styles.color) || "#1a1a1a",
    textAlign: parseAlignment(styles["text-align"]),
    fontFamily: parseFontFamily(styles["font-family"]) || undefined,
    styles: {
      padding: readPaddingFromStyles(styles),
    },
  });
}

/**
 * Whether a `style` attribute states an alignment of its own. `inherit` does
 * not: it takes the container's.
 */
function statesOwnAlignment(style: string): boolean {
  const own = parseStyleAttribute(style)["text-align"];
  return own !== undefined && own.trim().toLowerCase() !== "inherit";
}

/**
 * Apply a container-level `text-align` to every `<p>` opening tag in `html`
 * that states no alignment of its own, merging into an existing `style="…"`
 * attribute when present. Any other attribute on the `<p>` (class/id/dir/…)
 * is kept, and does not stop the alignment from applying.
 *
 * A `<p>`'s own `text-align` wins, as it does in a browser, where the
 * container's value only reaches a paragraph by inheritance.
 */
function applyTextAlignToParagraphs(html: string, textAlign: string): string {
  return html.replace(/<p\b([^>]*)>/gi, (match, attrs: string) => {
    const styleMatch = /\sstyle\s*=\s*"([^"]*)"/i.exec(attrs);
    if (styleMatch) {
      if (statesOwnAlignment(styleMatch[1])) return match;
      const existing = styleMatch[1].trim().replace(/;\s*$/, "");
      const merged = existing
        ? `${existing}; text-align: ${textAlign}`
        : `text-align: ${textAlign}`;
      const newAttrs =
        attrs.slice(0, styleMatch.index) +
        ` style="${merged}"` +
        attrs.slice(styleMatch.index + styleMatch[0].length);
      return `<p${newAttrs}>`;
    }
    return `<p${attrs} style="text-align: ${textAlign}">`;
  });
}

/**
 * Builds a Paragraph block from a fragment of inline markup, styled by the
 * element that supplied `styles`.
 *
 * `padding` defaults to that element's own. A caller building from a host's
 * styles passes none: a host insets every block it holds, and the cell walk
 * applies that inset once, to all of them.
 */
function buildParagraph(
  innerHtml: string,
  styles: Record<string, string>,
  padding: SpacingValue = readPaddingFromStyles(styles),
): Block {
  const wrapped = ensureParagraphWrapped(innerHtml);

  // Apply container-level styles to the wrapping <p>.
  const fontParts: string[] = [];
  const fontSize = parsePxValue(styles["font-size"]);
  if (fontSize && fontSize !== 16) fontParts.push(`font-size: ${fontSize}px`);
  const color = parseColor(styles.color);
  if (color && color !== "#1a1a1a") fontParts.push(`color: ${color}`);
  const fontWeight = parseFontWeight(styles["font-weight"]);
  if (fontWeight) fontParts.push(`font-weight: ${fontWeight}`);
  const fontFamily = parseFontFamily(styles["font-family"]);
  if (fontFamily) fontParts.push(`font-family: ${fontFamily}`);
  const textAlign = styles["text-align"];

  let result = wrapped;
  if (textAlign && textAlign !== "left") {
    result = applyTextAlignToParagraphs(result, textAlign);
  }
  if (fontParts.length > 0) {
    const span = fontParts.join("; ");
    result = result.replace(
      /<p([^>]*)>([\s\S]*?)<\/p>/g,
      `<p$1><span style="${span}">$2</span></p>`,
    );
  }

  return createParagraphBlock({
    content: result,
    styles: {
      padding,
    },
  });
}

/**
 * Paragraph or block-level text container → Paragraph block.
 */
function convertParagraph($el: Cheerio<Element>): Block {
  return buildParagraph(getInnerHtml($el), getStyles($el));
}

/**
 * Decides whether a child node of a table cell belongs to a run of inline
 * text rather than to a block of its own: a bare text node, or one of the
 * inline formatting tags.
 */
export function isInlineContent(node: AnyNode): boolean {
  if (isText(node)) return true;
  return isTag(node) && INLINE_FORMATTING_TAGS.has(node.tagName.toLowerCase());
}

/**
 * Converts a run of consecutive inline nodes lifted out of a table cell into
 * one Paragraph block, keeping their markup inside the paragraph's content.
 *
 * `$cell` supplies the styling: a bare run has no element of its own to read
 * a colour, size or alignment from, and table-based email puts all three on
 * the cell. Its padding stays out: the cell walk insets every block the cell
 * holds by it, so reading it here too would count it twice.
 *
 * Returns `null` for a run carrying no text — a cell holding nothing but
 * `&nbsp;` and `<br>` has no content, the same reading `convertElement`
 * gives an empty `<p>`.
 */
export function convertInlineRun(
  nodes: AnyNode[],
  $cell: Cheerio<Element>,
  $: CheerioAPI,
): { block: Block; entry: ImportReportEntry } | null {
  const text = nodes.map((node) => $(node).text()).join("");
  if (!text.trim()) return null;

  const html = nodes.map((node) => $.html(node)).join("");

  return {
    block: buildParagraph(html, getStyles($cell), emptyPadding()),
    entry: {
      sourceTag: tagOf($cell[0]),
      templaticalBlockType: "paragraph",
      status: "converted",
    },
  };
}

/**
 * <img> → Image block.
 */
function convertImage(
  $el: Cheerio<Element>,
  link?: { url: string; openInNewTab: boolean },
): Block {
  const styles = getStyles($el);
  const src = $el.attr("src") ?? "";
  const alt = $el.attr("alt") ?? "";
  const widthAttr = $el.attr("width");
  const widthStyle = styles.width;
  // Kept separate from the `?? 600` fallback: a percentage radius has to
  // resolve against a width the template actually stated.
  const readWidth =
    parsePxValue(widthAttr) || parsePxValue(widthStyle) || undefined;
  const width = readWidth ?? 600;
  // No default, unlike width: an absent height is what keeps the aspect ratio,
  // and `height="auto"` / `height: auto` parse to 0, which reads the same way.
  const height =
    parsePxValue($el.attr("height")) ||
    parsePxValue(styles.height) ||
    undefined;
  const href = (link?.url ?? "").trim();

  return createImageBlock({
    src,
    alt,
    width,
    height,
    borderRadius: parseImageBorderRadius(
      styles["border-radius"],
      readWidth,
      height,
    ),
    align: parseAlignment(styles["text-align"], "center"),
    styles: {
      padding: readPaddingFromStyles(styles),
    },
    ...(href ? { linkUrl: href } : {}),
    ...(href && link?.openInNewTab ? { linkOpenInNewTab: true } : {}),
  });
}

/**
 * Whether an `<a>` wraps an image. An anchor wrapping only an image carries
 * no text, so a button built from it is labelled by the factory default and
 * discards the image entirely. Asked before both button paths — `convertElement`
 * and `isButtonCell` — and before a mixed image-plus-text anchor can fold
 * into a prose run.
 */
export function isImageAnchor($el: Cheerio<Element>): boolean {
  return $el.find("img").length > 0;
}

function isImageOnlyAnchor($el: Cheerio<Element>): boolean {
  return isImageAnchor($el) && ($el.text() ?? "").trim() === "";
}

function convertLinkedImage(
  $anchor: Cheerio<Element>,
  $img: Cheerio<Element>,
): { block: Block; entry: ImportReportEntry } {
  return {
    block: convertImage($img, {
      url: $anchor.attr("href") ?? "",
      openInNewTab: $anchor.attr("target") === "_blank",
    }),
    entry: {
      sourceTag: "a",
      templaticalBlockType: "image",
      status: "converted",
    },
  };
}

/**
 * An `<a>` that wraps both an image and text cannot become one block without
 * dropping one of them: `convertElement` returns a single block. The walker
 * emits the image with `linkUrl` and a sibling paragraph that keeps the
 * remaining `<a>`, so neither is lost.
 */
function splitMixedImageAnchor(
  $anchor: Cheerio<Element>,
  $host: Cheerio<Element>,
  $: CheerioAPI,
): { block: Block; entry: ImportReportEntry }[] {
  const results: { block: Block; entry: ImportReportEntry }[] = [];

  for (const img of $anchor.find("img").toArray()) {
    results.push(
      convertLinkedImage($anchor, $(img) as unknown as Cheerio<Element>),
    );
  }

  const $clone = $anchor.clone();
  $clone.find("img").remove();
  if (($clone.text() ?? "").trim() === "") return results;

  results.push({
    block: buildParagraph(
      $.html($clone) ?? "",
      getStyles($host),
      emptyPadding(),
    ),
    entry: {
      sourceTag: tagOf($host[0]),
      templaticalBlockType: "paragraph",
      status: "converted",
    },
  });
  return results;
}

/**
 * <a> styled as a button → Button block.
 *
 * Heuristic: a single `<a>` with a non-transparent background-color OR padding
 * OR border-radius OR display: inline-block / block is treated as a button.
 */
export function looksLikeButton(styles: Record<string, string>): boolean {
  if (parseColor(styles["background-color"]) || parseColor(styles.background))
    return true;
  if (
    styles.padding ||
    styles["padding-top"] ||
    styles["padding-bottom"] ||
    styles["padding-left"] ||
    styles["padding-right"]
  )
    return true;
  if (parsePxValue(styles["border-radius"])) return true;
  const display = (styles.display ?? "").toLowerCase();
  if (display === "inline-block" || display === "block") return true;
  return false;
}

/**
 * Decides whether an `<a>` belongs to the run of prose around it rather than
 * to a block of its own: a link the source did not style as a button, whose
 * own text is what the reader sees.
 *
 * A link inside a sentence is prose, so folding it keeps the sentence in one
 * editable block — and keeps the anchor's markup, `href` included, which the
 * per-element path drops (`convertParagraph` reads inner HTML, so the element
 * itself never reaches the block).
 *
 * Three constraints, each a hazard a relaxed version would reintroduce:
 *
 * - `looksLikeButton` is the same predicate `convertElement` and
 *   `isButtonCell` use to tell a call to action from a link, so a styled
 *   anchor is never absorbed into a sentence and keeps becoming a button.
 * - The anchor must carry text. `convertInlineRun` reads a run with no text
 *   as empty and emits nothing, so an anchor whose content is an image has to
 *   keep the block it already gets; folding it would delete the image.
 * - The anchor must not wrap an image. An image-plus-text `<a>` folded into
 *   the run would keep the image as raw markup; it is a first-class image
 *   with `linkUrl` and a sibling paragraph instead.
 */
export function isProseAnchor($el: Cheerio<Element>): boolean {
  if (looksLikeButton(getStyles($el))) return false;
  if (isImageAnchor($el)) return false;
  return ($el.text() ?? "").trim() !== "";
}

/**
 * Walks an element's child *nodes*, grouping consecutive inline content into
 * runs and handing every other element to the caller.
 *
 * A bare text node between two elements is content, and a walk over
 * `children()` never visits it — so `Hello<br>World` loses both words while
 * emitting a block holding nothing but `<br>`. Consecutive inline nodes
 * therefore accumulate into one run and become a single paragraph, which is
 * what makes a bare line agree with the same line wrapped in a `<p>`.
 *
 * One walker for all three content traversals — the body walk, the layout
 * container walk and the cell walk — because a bare text node is content
 * wherever it sits and the three have to read it identically. A second copy
 * of this classification is the hazard: fixing it on one surface leaves the
 * others silently dropping copy the source email displays, and nothing fails
 * to say so. What differs between the three is only what a *block-level*
 * element becomes, which is why that half is the caller's.
 *
 * `$host` is what a run reads its styling and source tag from: a bare run has
 * no element of its own, and the nearest enclosing element is the one carrying
 * the colour, size and alignment it renders with.
 *
 * A run never reaches across `$host`'s own children into a descendant's:
 * `onElement` is called with the run already flushed, so a caller that
 * recurses keeps the text before a block-level child ahead of it.
 */
export function walkContentNodes(
  $host: Cheerio<Element>,
  $: CheerioAPI,
  onRun: (converted: { block: Block; entry: ImportReportEntry }) => void,
  onElement: ($child: Cheerio<Element>, tag: string) => void,
): void {
  let inlineRun: AnyNode[] = [];

  const flushInlineRun = () => {
    if (inlineRun.length === 0) return;
    const run = inlineRun;
    inlineRun = [];
    const converted = convertInlineRun(run, $host, $);
    if (converted) onRun(converted);
  };

  for (const node of $host.contents().toArray()) {
    if (isInlineContent(node)) {
      inlineRun.push(node);
      continue;
    }
    // Comments and processing instructions carry no content, and must not end
    // the run either: a merge-tag comment sitting mid-sentence would otherwise
    // split one line into two paragraphs.
    if (!isTag(node)) continue;

    const $child = $(node) as unknown as Cheerio<Element>;
    const tag = node.tagName.toLowerCase();

    // A link inside a sentence is part of that sentence, so it joins the run
    // rather than ending it: one rich-text block carries the whole line, with
    // the anchor's own markup inside it. The per-element path builds its
    // paragraph from inner HTML, so the `<a>` never reaches the block and the
    // `href` is lost.
    //
    // Asked before the run is flushed and before the caller sees the element,
    // which is what keeps a call to action out of a sentence — a styled anchor
    // is not a prose anchor, so it reaches `onElement` and becomes its button.
    //
    // An image-plus-text `<a>` is not one block: `convertElement` would drop
    // either the image or the text. Split it here so every walk — cell, body,
    // container — keeps both.
    if (tag === "a" && isImageAnchor($child) && ($child.text() ?? "").trim()) {
      flushInlineRun();
      for (const converted of splitMixedImageAnchor($child, $host, $)) {
        onRun(converted);
      }
      continue;
    }

    if (tag === "a" && isProseAnchor($child)) {
      inlineRun.push(node);
      continue;
    }

    flushInlineRun();
    onElement($child, tag);
  }

  flushInlineRun();
}

/**
 * Reads a button's placement from the cell that wraps it. An anchor styled as
 * a button is sized to its own content, so its `text-align` says nothing about
 * where it sits — table-based email puts that on the containing `<td>`, as
 * either a `text-align` style or the legacy `align` attribute. Only the
 * immediate parent is consulted; walking further up would start reporting the
 * alignment of the surrounding layout rather than of the button.
 */
function readButtonAlign($el: Cheerio<Element>): "left" | "center" | "right" {
  const parent = $el.parent();
  if (parent.length === 0) return "center";

  const fromStyle = parseStyleAttribute(parent.attr("style"))["text-align"];
  if (fromStyle) return parseAlignment(fromStyle, "center");

  return parseAlignment(parent.attr("align"), "center");
}

function convertButton($el: Cheerio<Element>): Block {
  const styles = getStyles($el);
  const text = ($el.text() ?? "Button").trim() || "Button";
  const url = $el.attr("href") ?? "#";
  const target = $el.attr("target");

  return createButtonBlock({
    text,
    url,
    openInNewTab: target === "_blank" || undefined,
    backgroundColor:
      parseColor(styles["background-color"]) ||
      parseColor(styles.background) ||
      "#4f46e5",
    textColor: parseColor(styles.color) || "#ffffff",
    borderRadius: parsePxValue(styles["border-radius"]),
    fontSize: parsePxValue(styles["font-size"]) || 16,
    fontFamily: parseFontFamily(styles["font-family"]) || undefined,
    align: readButtonAlign($el),
    buttonPadding: readPaddingFromStyles(styles),
    styles: {
      padding: emptyPadding(),
    },
  });
}

/** A width as a number and an optional unit; a unitless value is px. */
const DIVIDER_WIDTH = /^(-?\d+(?:\.\d+)?)\s*(%|px)?$/i;

/**
 * The width an `<hr>` spans, from its `width` style, then its `width`
 * attribute, with a note in `notes` for whatever the result does not keep.
 *
 * No width, `auto` and `100%` are the whole column, which is how a
 * block-level `<hr>` renders. Any other percentage stays a share of the
 * column, clamped to 0–100% and rounded to two decimals, which keeps it inside
 * `DividerPercentWidth`'s pattern. A px width stays px until it reaches
 * `room` less the divider's own side padding, which is the width `mj-divider`
 * draws 100% across; past that it is the whole column, and it keeps narrowing
 * with the column on a phone. An unknown `room` keeps every px width.
 */
function readDividerWidth(
  $el: Cheerio<Element>,
  room: number | undefined,
  notes: string[],
): DividerBlock["width"] {
  const styles = getStyles($el);
  const raw = (styles.width ?? $el.attr("width") ?? "").trim();
  if (raw === "" || raw.toLowerCase() === "auto") return "full";

  const match = raw.match(DIVIDER_WIDTH);
  if (!match) {
    notes.push(
      `Divider width "${raw}" could not be read; imported as full width.`,
    );
    return "full";
  }

  const value = parseFloat(match[1]);
  if (match[2] === "%") {
    const percent = Math.min(100, Math.max(0, value));
    if (percent !== value) {
      notes.push(`Divider width ${raw} was clamped to ${percent}%.`);
    }
    const share = Math.round(percent * 100) / 100;
    return share === 100 ? "full" : `${share}%`;
  }

  const px = Math.max(0, Math.round(value));
  if (value < 0) notes.push(`Divider width ${raw} was clamped to 0px.`);
  if (room === undefined) return px;

  const own = readPaddingFromStyles(styles);
  return px >= room - own.left - own.right ? "full" : px;
}

/**
 * Where an `<hr>` sits in its column. Each side margin starts at the
 * browser's `auto`, the legacy `align` attribute sets them next, and CSS
 * margins override both, side by side. Auto on both sides centres the line;
 * auto on the left alone pushes it right; anything else leaves it at the
 * start of the column.
 */
function dividerAlign($el: Cheerio<Element>): "left" | "center" | "right" {
  let left = "auto";
  let right = "auto";

  const align = ($el.attr("align") ?? "").trim().toLowerCase();
  if (align === "left") left = "0";
  if (align === "right") right = "0";

  const styles = getStyles($el);
  const margin = (styles.margin ?? "").trim().split(/\s+/).filter(Boolean);
  if (margin.length > 0) {
    right = margin[1] ?? margin[0];
    left = margin.length === 4 ? margin[3] : right;
  }
  left = styles["margin-left"] ?? left;
  right = styles["margin-right"] ?? right;

  const isAuto = (value: string) => value.trim().toLowerCase() === "auto";
  if (isAuto(left) && isAuto(right)) return "center";
  return isAuto(left) ? "right" : "left";
}

/**
 * <hr> → Divider block, with a note for each thing the block does not keep.
 *
 * Templatical centres every divider, so a partial one the source places at
 * either side is reported. A full-width line has no placement to lose.
 */
function convertDivider(
  $el: Cheerio<Element>,
  room: number | undefined,
): { block: Block; notes: string[] } {
  const styles = getStyles($el);
  const border = parseBorderShorthand(styles["border-top"] ?? styles.border);
  const lineStyle =
    border.style === "dashed" || border.style === "dotted"
      ? border.style
      : "solid";

  const notes: string[] = [];
  const width = readDividerWidth($el, room, notes);
  const align = dividerAlign($el);
  if (width !== "full" && align !== "center") {
    notes.push(
      `The source aligns this divider ${align}; Templatical centres every divider.`,
    );
  }

  return {
    block: createDividerBlock({
      lineStyle: lineStyle as "solid" | "dashed" | "dotted",
      color: border.color || "#e5e7eb",
      thickness: border.width || 1,
      width,
      styles: {
        padding: readPaddingFromStyles(styles),
      },
    }),
    notes,
  };
}

/**
 * Wraps the element's outerHTML in an HTML block (the lossless fallback).
 */
export function convertHtmlFallback(
  $el: Cheerio<Element>,
  $: CheerioAPI,
  note?: string,
): Block {
  const outer = $.html($el) ?? "";
  const content = note ? safeHtmlComment(note, outer) : outer;
  const styles = getStyles($el);

  return createHtmlBlock({
    content,
    styles: {
      padding: readPaddingFromStyles(styles),
    },
  });
}

/**
 * Decides whether a `<td>` / `<th>` carries nothing a reader would see: no
 * text once source whitespace and `&nbsp;` are collapsed, and no element that
 * renders on its own.
 *
 * Text alone is not the test. An image or a link carries no text and is
 * content all the same, so a cell holding one is never blank — reading it as
 * blank would make a picture-only column disappear.
 *
 * Lives here, with the other predicates both traversal modules consult,
 * because a spacer cell and a row's gutter cells are one fact read for two
 * purposes: `isSpacerCell` adds a stated height to it, and the section
 * builder reads a row's blank cells as chrome rather than as columns. A
 * second copy would let one cell be a spacer in one traversal and a column
 * in the other.
 */
export function isBlankCell($el: Cheerio<Element>): boolean {
  if (normalizeCellText($el.text() ?? "") !== "") return false;
  return $el.find("img, a, hr").length === 0;
}

/**
 * Decides whether a `<td>` looks like a vertical spacer: blank, and carrying
 * an explicit height.
 */
export function isSpacerCell($el: Cheerio<Element>): boolean {
  if (!isBlankCell($el)) return false;

  const styles = getStyles($el);
  const hasHeight =
    parsePxValue($el.attr("height")) > 0 ||
    parsePxValue(styles.height) > 0 ||
    parsePxValue(styles["line-height"]) > 0;
  return hasHeight;
}

/**
 * Collapses every run of whitespace — `&nbsp;` included — to one space and
 * trims. Source indentation and nested tags introduce whitespace that never
 * renders, so a text comparison has to normalise both sides.
 */
function normalizeCellText(value: string): string {
  return value.replace(/[\s\u00a0]+/g, " ").trim();
}

/**
 * Whether the anchor *is* the cell rather than sitting inside its content.
 *
 * The hazard this guards: `buildCellButton` labels the button with the
 * anchor's text and drops every other node in the cell, so classifying a
 * sentence that merely contains a link as a button deletes the sentence. The
 * constraint is that a cell only reads as a button when the link is its
 * entire content — and `find("a")` matches at any depth, so an outer callout
 * cell wrapping a real CTA reaches the same test.
 */
function isWholeCellAnchor(
  $el: Cheerio<Element>,
  $anchor: Cheerio<Element>,
): boolean {
  return (
    normalizeCellText($el.text() ?? "") ===
    normalizeCellText($anchor.text() ?? "")
  );
}

/**
 * Decides whether a `<td>` is a button container — i.e. its entire content is
 * one `<a>`, styled as a button either on the anchor or on the cell.
 *
 * Both arms require the anchor to be the cell's whole content. The anchor's
 * own styling is the stronger signal that a link is *a button*, but it says
 * nothing about whether the link is *the cell*, and `find("a")` matches at
 * any depth — so a callout cell holding a paragraph plus a self-styled CTA
 * satisfies the anchor arm exactly as it does the cell arm.
 */
export function isButtonCell(
  $el: Cheerio<Element>,
  $: CheerioAPI,
): { match: boolean; anchor?: Cheerio<Element> } {
  const anchors = $el.find("a");
  if (anchors.length !== 1) return { match: false };
  const anchor = $(anchors[0]);
  // An image-only `<a>` is `"" === ""` on the whole-cell text test, and a
  // button labelled from that empty text is the factory default — the image
  // is discarded. Mixed image-plus-text is the same loss of the image.
  if (isImageAnchor(anchor)) return { match: false };
  if (!isWholeCellAnchor($el, anchor)) return { match: false };

  if (looksLikeButton(getStyles(anchor))) return { match: true, anchor };
  // Cell-level styling (bg, padding) wrapping a plain anchor reads as a
  // button only when the anchor actually has an href. Without one, the
  // anchor is a decorative styled span and should fall through to the
  // text-conversion path; otherwise convertButton defaults href to "#"
  // and the import becomes a clickable button to nowhere.
  if (looksLikeButton(getStyles($el))) {
    const href = (anchor.attr("href") ?? "").trim();
    if (href !== "") {
      return { match: true, anchor };
    }
  }
  return { match: false };
}

/**
 * Converts a single content-bearing element (heading / paragraph / image /
 * anchor-as-button / divider) to a Templatical block.
 *
 * A container whose entire meaningful content is one block-level element is
 * unwrapped first, so the element inside is what gets mapped and named in the
 * report — see `resolveWrappedBlock`. Only a heading can come back from that,
 * which is why the heading branch is the only one taking the resolved styles:
 * for every other branch the resolved element is the one handed in, so its own
 * styles are what `styles` already holds.
 *
 * `room` is the width, in px, that a line in the element's column can span
 * before the element's own padding, when the caller knows it. Only a divider
 * reads it.
 *
 * Returns `null` for elements that do not contain any meaningful content
 * (the caller should skip them).
 */
export function convertElement(
  $el: Cheerio<Element>,
  $: CheerioAPI,
  room?: number,
): { block: Block; entry: ImportReportEntry } | null {
  const { $el: $target, styles } = resolveWrappedBlock($el, $);
  const tag = tagOf($target[0]);
  if (!tag) return null;

  if (HEADING_TAGS.has(tag)) {
    return {
      block: convertHeading($target, styles),
      entry: {
        sourceTag: tag,
        templaticalBlockType: "title",
        status: "converted",
      },
    };
  }

  if (tag === "img") {
    return {
      block: convertImage($target),
      entry: {
        sourceTag: tag,
        templaticalBlockType: "image",
        status: "converted",
      },
    };
  }

  if (tag === "a") {
    // Image-only is decided before looksLikeButton: an image-only anchor
    // carries no text, so a button built from it is labelled by the factory
    // default and discards the image entirely.
    if (isImageOnlyAnchor($target)) {
      return convertLinkedImage(
        $target,
        $($target.find("img")[0]) as unknown as Cheerio<Element>,
      );
    }
    if (looksLikeButton(styles)) {
      return {
        block: convertButton($target),
        entry: {
          sourceTag: tag,
          templaticalBlockType: "button",
          status: "converted",
        },
      };
    }
    // Plain anchor — wrap as paragraph.
    return {
      block: convertParagraph($target),
      entry: {
        sourceTag: tag,
        templaticalBlockType: "paragraph",
        status: "approximated",
        note: "Inline anchor wrapped in a paragraph block.",
      },
    };
  }

  if (tag === "hr") {
    const { block, notes } = convertDivider($target, room);
    return {
      block,
      entry: {
        sourceTag: tag,
        templaticalBlockType: "divider",
        status: notes.length > 0 ? "approximated" : "converted",
        ...(notes.length > 0 ? { note: notes.join(" ") } : {}),
      },
    };
  }

  if (TEXT_TAGS.has(tag)) {
    if (!hasRenderedContent($target)) return null;
    return {
      block: convertParagraph($target),
      entry: {
        sourceTag: tag,
        templaticalBlockType: "paragraph",
        status: "converted",
      },
    };
  }

  // Unknown element — preserve as HTML.
  return {
    block: convertHtmlFallback(
      $target,
      $,
      `Unsupported element <${tag}>: preserved as raw HTML`,
    ),
    entry: {
      sourceTag: tag,
      templaticalBlockType: "html",
      status: "html-fallback",
      note: `Unknown element "${tag}" preserved as HTML block.`,
    },
  };
}
